import { Link } from "react-router-dom";
import { ArrowRight, Building2, Building, Construction, Heart, GraduationCap, Factory, Users, Briefcase, HardHat, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";
import { commercialProjects } from "../mockData";

export const Commercial = () => {
  return (
    <div className="min-h-screen bg-navy-900 pt-20">
      <SEO 
        title="Commercial Projects | Magic C Plumbing | Plumber & Heating Engineer in London"
        description="Trusted partner for architects, developers, hotels, and commercial properties. Expert subcontracting and high-end plumbing solutions for large luxury projects."
        canonical="/commercial"
      />
      {/* Hero */}
      <section className="py-24 bg-gradient-to-b from-navy-900 to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Commercial & Professional Services
            </h1>
            <p className="text-2xl text-gray-300 leading-relaxed">
              Large-scale plumbing solutions for businesses, developers, and professional partners
            </p>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 mb-24">
            {[
              {
                icon: Building2,
                title: "Large Projects",
                description: "Complete plumbing and heating solutions for large commercial buildings, multi-unit developments, and major renovations",
                features: [
                  "Project management from start to finish",
                  "Dedicated site teams",
                  "Flexible scheduling to meet deadlines",
                  "Comprehensive warranties"
                ]
              },
              {
                icon: HardHat,
                title: "Subcontracting",
                description: "Reliable subcontracting services for main contractors with proven track record and quality assurance",
                features: [
                  "Fully insured and certified",
                  "Experienced site supervisors",
                  "Detailed quotations and planning",
                  "On-time, on-budget delivery"
                ]
              },
              {
                icon: Briefcase,
                title: "Architects & Developers",
                description: "Collaborative partnerships with design professionals for seamless project execution",
                features: [
                  "Early design consultation",
                  "Technical drawings and specifications",
                  "Value engineering support",
                  "Post-completion support"
                ]
              },
              {
                icon: Users,
                title: "Corporate Clients",
                description: "Ongoing maintenance and emergency services for corporate facilities and office buildings",
                features: [
                  "24/7 emergency response",
                  "Preventive maintenance programs",
                  "Dedicated account management",
                  "Flexible payment terms"
                ]
              }
            ].map((category) => {
              const Icon = category.icon;
              return (
                <Card key={category.title} className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all">
                  <CardContent className="p-10">
                    <div className="bg-sage-500/20 w-20 h-20 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-md border border-sage-500/30">
                      <Icon className="h-10 w-10 text-sage-400" />
                    </div>
                    <h3 className="text-3xl font-bold text-white mb-4">{category.title}</h3>
                    <p className="text-gray-300 text-lg mb-6 leading-relaxed">{category.description}</p>
                    <ul className="space-y-3">
                      {category.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <CheckCircle className="h-6 w-6 text-sage-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-200">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-24 bg-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our Commercial Expertise
            </h2>
            <p className="text-xl text-gray-300">
              Trusted by leading organizations across multiple sectors
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {commercialProjects.map((project) => {
              const IconComponent = {
                Building2, Building, Construction, Heart, GraduationCap, Factory
              }[project.icon];
              
              return (
                <Card key={project.id} className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all group">
                  <CardContent className="p-8">
                    <div className="bg-sage-500/20 group-hover:bg-sage-500 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all">
                      <IconComponent className="h-8 w-8 text-sage-300 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {project.title}
                    </h3>
                    <p className="text-gray-300 mb-4 leading-relaxed">
                      {project.description}
                    </p>
                    <div className="flex items-center space-x-2 text-sage-300">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-semibold">{project.projects}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-navy-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Discuss Your Commercial Project?
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10">
            Our commercial team is ready to provide detailed quotations, technical specifications, and project planning support
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-sage-600 hover:bg-sage-700 text-white text-lg px-10 py-7">
                Join Our Satisfied Clients
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <a href="tel:03337890987">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-navy-900 text-lg px-10 py-7">
                Call Commercial Team
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
